<template>
  <div>
    <v-btn
      v-if="isReady && !showAiAssistantIframe"
      fab
      color="profileAnchorColor"
      class="ai-assistant-fab"
      :class="{ 'is-above-editor-bottombar': isEditorRoute }"
      @click="showAiAssistantIframe = true"
    >
      <i class="material-icons ai-assistant-fab-icon">chat</i>
    </v-btn>

    <div
      v-if="isReady && showAiAssistantIframe"
      class="ai-assistant-iframe-overlay"
      :class="{
        'is-chatbox': isChatbox,
        'is-floating': isChatbox && isFloating,
        'is-dragging': dragState !== null,
      }"
      :style="overlayStyle"
    >
      <div
        class="ai-assistant-iframe-topbar"
        :class="{ 'is-draggable': isChatbox }"
        @mousedown="onTopbarMouseDown"
        @dblclick="onTopbarDoubleClick"
      >
        <img
          src="/static/POP Pilot Logo.png"
          alt="POP Pilot"
          class="ai-assistant-iframe-logo"
        />
        <div class="ai-assistant-iframe-actions">
          <button
            type="button"
            class="ai-assistant-iframe-action"
            :aria-label="isChatbox ? 'Expand' : 'Minimize to chat box'"
            @click="toggleChatbox"
          >
            <i class="material-icons">
              {{ isChatbox ? 'open_in_full' : 'close_fullscreen' }}
            </i>
          </button>
          <button
            type="button"
            class="ai-assistant-iframe-action"
            aria-label="Close"
            @click="showAiAssistantIframe = false"
          >
            <i class="material-icons">close</i>
          </button>
        </div>
      </div>
      <iframe
        ref="popAssistantIframe"
        :src="iframeSrc"
        class="ai-assistant-iframe"
        :style="iframePointerStyle"
        frameborder="0"
        allow="clipboard-write"
        @load="onIframeLoad"
      ></iframe>
      <div
        v-if="isChatbox"
        class="ai-assistant-resize-handle handle-left"
        @mousedown="onLeftResizeMouseDown"
      ></div>
      <div
        v-if="isChatbox && isFloating"
        class="ai-assistant-resize-handle handle-top"
        @mousedown="onTopResizeMouseDown"
      ></div>
      <div
        v-if="isChatbox && isFloating"
        class="ai-assistant-resize-handle handle-right"
        @mousedown="onRightResizeMouseDown"
      ></div>
      <div
        v-if="isChatbox && isFloating"
        class="ai-assistant-resize-handle handle-bottom"
        @mousedown="onBottomResizeMouseDown"
      ></div>
      <div
        v-if="isChatbox && isFloating"
        class="ai-assistant-resize-handle handle-corner"
        @mousedown="onCornerResizeMouseDown"
      ></div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';

const STORAGE_KEY = 'pop-assistant-window-state';
const SETTINGS_KEY = 'popAssistantWindowState';
const DEFAULT_WIDTH = 900;
const MIN_WIDTH = 280;
const MIN_HEIGHT = 240;
const SNAP_THRESHOLD = 24;

const DEFAULT_STATE = {
  open: false,
  // null = no saved mode yet; the route decides the default (see defaultMode).
  mode: null,
  docked: 'right',
  width: DEFAULT_WIDTH,
  height: null,
  x: null,
  y: null,
};

export default {
  name: "PopAssistantButton",
  props: {
    // True on routes that render the app sidebar (<app-menu>). Drives the
    // default view mode until the user has a persisted preference.
    routeShowsMenu: { type: Boolean, default: true },
  },
  data() {
    return {
      showAiAssistantIframe: false,
      mainAreaRect: null,
      mode: 'fullscreen',
      docked: 'right',
      sidebarWidth: DEFAULT_WIDTH,
      floatingHeight: null,
      floatingX: null,
      floatingY: null,
      dragState: null,
      hydrating: false,
      settingsHydrated: false,
      // While true, the mode follows the route default (no saved mode yet).
      routeDefaultActive: false,
    };
  },
  computed: {
    POP_ASSISTANT_BASE_URL() {
      return POP_ASSISTANT_BASE_URL;
    },
    iframeSrc() {
      // Anonymous embeds carry ?surface=support-sales, the value the marketing
      // site uses for its non-homepage pages: Rocky renders its internal
      // support/sales welcome screen for it (anything else, including 'login',
      // falls back to the homepage embed look). Constant per instance so the
      // iframe never reloads on navigation between the anonymous views. Only
      // the origin is used for the postMessage targetOrigin and the inbound
      // event.origin check, so the query string doesn't affect them. App.vue
      // remounts this component on login, so an authenticated instance never
      // carries it.
      return this.isLoggedIn
        ? POP_ASSISTANT_BASE_URL
        : `${POP_ASSISTANT_BASE_URL}/?surface=support-sales`;
    },
    currentUser() {
      return this.$store.state.user;
    },
    isLoggedIn() {
      return this.$store.getters.isAuthenticated;
    },
    userSettings() {
      const user = this.$store.state.user;
      return user && user.settings ? user.settings : {};
    },
    hasAccessToRocky() {
      // Authenticated: the backend flag (currently true for every logged-in
      // user). Anonymous: the login/register views (store isLoginOrRegister),
      // where Rocky runs in its anonymous sales mode; independent pages stay
      // without it. App.vue's shouldShowTawk is the exact complement of this
      // in production.
      if (this.isLoggedIn) return this.$store.getters.hasAccessToRocky;
      return this.$store.getters.isLoginOrRegister(this.$route.name);
    },
    isReady() {
      // Gate the button/iframe until hydration has run and the user is
      // authorized to use Rocky.
      return this.settingsHydrated && this.hasAccessToRocky;
    },
    currentToken() {
      return this.$store.getters.getToken;
    },
    subAccountRelationId() {
      const rel = this.$store.state.selectedSubAccountRelation;
      return rel && rel.id ? rel.id : null;
    },
    defaultMode() {
      // Routes with the app sidebar default to fullscreen; menu-less routes
      // (login, register, setup, independent pages) default to chatbox.
      return this.routeShowsMenu ? 'fullscreen' : 'chatbox';
    },
    isEditorRoute() {
      return this.$route.name === 'FullPageSourceEditor';
    },
    isChatbox() {
      return this.mode === 'chatbox';
    },
    isFloating() {
      return this.docked === null;
    },
    iframePointerStyle() {
      return this.dragState ? { pointerEvents: 'none' } : {};
    },
    overlayStyle() {
      if (!this.mainAreaRect) return {};
      const r = this.mainAreaRect;
      if (!this.isChatbox) {
        return {
          top: `${r.top}px`,
          left: `${r.left}px`,
          width: `${r.width}px`,
          height: `${r.height}px`,
        };
      }
      const w = Math.min(Math.max(this.sidebarWidth, MIN_WIDTH), r.width);
      if (this.docked === 'right') {
        return {
          top: `${r.top}px`,
          left: `${r.right - w}px`,
          width: `${w}px`,
          height: `${r.height}px`,
        };
      }
      const h = this.floatingHeight !== null
        ? Math.min(Math.max(this.floatingHeight, MIN_HEIGHT), window.innerHeight)
        : r.height;
      const rawX = this.floatingX !== null ? this.floatingX : r.right - w;
      const rawY = this.floatingY !== null ? this.floatingY : r.top;
      // Keep the topbar at least partially visible inside the main area.
      const minVisible = 80;
      const x = Math.min(Math.max(rawX, r.left - w + minVisible), r.right - minVisible);
      const y = Math.min(Math.max(rawY, r.top), r.bottom - 40);
      return {
        top: `${y}px`,
        left: `${x}px`,
        width: `${w}px`,
        height: `${h}px`,
      };
    },
  },
  watch: {
    showAiAssistantIframe(val) {
      if (val) {
        this.applyMainAreaSqueeze();
        // applyMainAreaSqueeze (not just updateMainAreaRect) so the padding
        // re-clamps when the viewport shrinks below the saved sidebar width.
        window.addEventListener('resize', this.applyMainAreaSqueeze);
        this.setupLayoutObservers();
      } else {
        this.releaseMainAreaSqueeze();
        window.removeEventListener('resize', this.applyMainAreaSqueeze);
        this.teardownLayoutObservers();
      }
      if (!this.hydrating) this.saveWindowState();
    },
    mode() {
      if (this.showAiAssistantIframe) this.applyMainAreaSqueeze();
      if (this.hydrating) return;
      this.saveWindowState();
      this.sendLayoutToIframe();
    },
    defaultMode(mode) {
      // No explicit choice yet → keep the active mode synced to the route
      // default, including across vue-router navigation. The `mode` watcher
      // then resizes the overlay, persists it (as null), and updates the
      // iframe. An explicit toggle clears routeDefaultActive and stops this.
      if (this.routeDefaultActive && this.settingsHydrated) this.mode = mode;
    },
    docked() {
      if (this.showAiAssistantIframe) this.applyMainAreaSqueeze();
      if (this.hydrating) return;
      this.saveWindowState();
      this.sendLayoutToIframe();
    },
    currentUser: {
      deep: true,
      handler() {
        this.postAuthToIframe();
        // Authenticated hard refresh: the user object arrives after mount
        // (router guard → loadUser), so hydrate window state from account
        // settings once it lands. The anonymous ↔ authenticated transition
        // never happens inside one instance: App.vue keys this component on
        // auth state and remounts it.
        if (!this.settingsHydrated) this.hydrateFromPersistence();
      },
    },
    currentToken() {
      this.postAuthToIframe();
    },
    subAccountRelationId() {
      this.postAuthToIframe();
    },
    $route(to) {
      if (!this.showAiAssistantIframe) return;
      // Re-apply (not just re-measure): the new route may render a different
      // main-area element that hasn't been padded yet.
      this.$nextTick(this.applyMainAreaSqueeze);
      this.postToIframe('pop-navigation');
      this.postContextToIframe(to);
    },
  },
  created() {
    // Debounce server writes — saveWindowState fires on every drag/resize.
    this.debouncedServerSave = debounce(this.saveStateToServer, 1000);
    // Dedupe signatures (non-reactive): last auth identity posted to Rocky and
    // last window state persisted. Prevent a settings-save → setUser → re-auth
    // → resize feedback loop that floods Rocky and trips its rate limits.
    this._lastAuthSig = null;
    this._lastSavedStateStr = null;
    // Latest screen-ready message (non-reactive), replayed on iframe load so
    // opening Rocky after a wizard emitted its state doesn't lose that state.
    this._lastScreenReady = null;
    // Debounced layout-shift re-measure; both layout observers funnel through
    // this so banners (Appcues, trial topbar) can't leave a stale rect behind.
    this.debouncedLayoutShift = debounce(this.handleLayoutShift, 100);
    this._mainAreaResizeObserver = null;
    this._bodyMutationObserver = null;
    this._observedMainAreaEl = null;
    this._rectSettleTimers = [];
  },
  mounted() {
    // The root instance renders via `render: h => h(App)` and has no template,
    // so the App.vue template ref never lands on $root.$refs. Components reach
    // this instance via $root.$refs.popAssistantButton (AiVisibilitySetupDialog,
    // NewPage wizard chips, rockyReadback) — register it here explicitly.
    this.$root.$refs.popAssistantButton = this;
    window.addEventListener('message', this.handleIframeMessage);
    this.hydrateFromPersistence();
  },
  beforeDestroy() {
    if (this.$root.$refs.popAssistantButton === this) {
      delete this.$root.$refs.popAssistantButton;
    }
    this.releaseMainAreaSqueeze();
    this.teardownLayoutObservers();
    window.removeEventListener('resize', this.applyMainAreaSqueeze);
    window.removeEventListener('message', this.handleIframeMessage);
    window.removeEventListener('mousemove', this.onDragMove);
    window.removeEventListener('mouseup', this.onDragEnd);
    if (this.debouncedServerSave) this.debouncedServerSave.cancel();
  },
  methods: {
    loadWindowState() {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...DEFAULT_STATE };
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_STATE, ...parsed };
      } catch (e) {
        return { ...DEFAULT_STATE };
      }
    },
    readPersistedState() {
      // Logged-in users read ONLY from their account settings — the source of
      // truth. When no setting exists (e.g. after a reset), start from defaults
      // so the route default applies. We must NOT fall back to a stale
      // anonymous localStorage blob here: it would resurrect an old mode and
      // get re-POSTed onto the freshly reset account on the next save.
      if (this.isLoggedIn) {
        const fromServer = this.userSettings[SETTINGS_KEY];
        if (fromServer && typeof fromServer === 'object') {
          return { ...DEFAULT_STATE, ...fromServer };
        }
        return { ...DEFAULT_STATE };
      }
      return this.loadWindowState();
    },
    hydrateFromPersistence() {
      // For logged-in users the user object loads after mount — defer until
      // it's available so we hydrate from server settings, not stale defaults.
      if (this.isLoggedIn && this.isUserStateEmpty()) return;
      this.hydrating = true;
      // No window state ever saved on this account → first login: auto-open
      // docked as a right sidebar. Gated on hasAccessToRocky so live-mode
      // users without access don't persist open:true for a hidden panel.
      const saved = this.userSettings[SETTINGS_KEY];
      const noSavedState = !(saved && typeof saved === 'object');
      // Accounts from the signup redesign get the onboarding modal and the FAB only: the docked
      // auto-open was existing accounts' first look at POP Pilot, and half a screen of Rocky on
      // top of the first dashboard visit is too much. Their closed state is persisted below so a
      // later login does not count as a first run either.
      const user = this.$store.state.user || {};
      const newAccount = Number(user.signupFlowVersion) >= 2 || user.welcomeCompleted === false;
      const firstRun = this.isLoggedIn && noSavedState && this.hasAccessToRocky && !newAccount;
      const restored = this.readPersistedState();
      const savedMode = restored.mode === 'chatbox' || restored.mode === 'fullscreen'
        ? restored.mode
        : null;
      // No saved mode → follow the route default (and keep following it on
      // navigation until the user persists a preference). The first-run
      // sidebar counts as an explicit choice so the defaultMode watcher
      // can't flip it to fullscreen and the save persists a concrete mode.
      this.routeDefaultActive = firstRun ? false : savedMode === null;
      this.mode = firstRun ? 'chatbox' : (savedMode || this.defaultMode);
      this.docked = restored.docked === null ? null : 'right';
      this.sidebarWidth = typeof restored.width === 'number' && restored.width > 0
        ? restored.width
        : DEFAULT_WIDTH;
      this.floatingHeight = typeof restored.height === 'number' ? restored.height : null;
      this.floatingX = typeof restored.x === 'number' ? restored.x : null;
      this.floatingY = typeof restored.y === 'number' ? restored.y : null;
      this.settingsHydrated = true;
      this.$nextTick(() => {
        this.hydrating = false;
        // hasAccessToRocky guard: an anonymous blob with open:true (written on
        // the login/register views) must not open the panel on a route where
        // nothing renders (e.g. a clearStore without navigation on /sites) —
        // the showAiAssistantIframe watcher would pad the main area for an
        // invisible panel.
        if ((restored.open || firstRun) && this.hasAccessToRocky) {
          this.showAiAssistantIframe = true;
        } else if (this.isLoggedIn && noSavedState && newAccount && this.hasAccessToRocky) {
          this.saveWindowState(); // closed FAB-only state becomes the account's saved preference
        }
      });
    },
    isUserStateEmpty() {
      const user = this.$store.state.user;
      return !user || Object.keys(user).length === 0;
    },
    saveWindowState() {
      const state = {
        open: this.showAiAssistantIframe,
        // While following the route default (no explicit choice yet), persist
        // null so a reload re-derives the default rather than reading back a
        // concrete mode. An explicit toggle clears routeDefaultActive first.
        mode: this.routeDefaultActive ? null : this.mode,
        docked: this.docked,
        width: this.sidebarWidth,
        height: this.floatingHeight,
        x: this.floatingX,
        y: this.floatingY,
      };
      const serialized = JSON.stringify(state);
      // Skip when nothing changed — Rocky repeats resize messages, which would
      // otherwise produce an endless stream of identical saves.
      if (serialized === this._lastSavedStateStr) return;
      this._lastSavedStateStr = serialized;
      if (this.isLoggedIn) {
        // Logged-in: persist (debounced) to the server-side settings dict.
        this.debouncedServerSave(state);
        return;
      }
      try {
        window.localStorage.setItem(STORAGE_KEY, serialized);
      } catch (e) {
        // ignore quota / privacy-mode errors
      }
    },
    saveStateToServer(windowState) {
      // The settings endpoint replaces the whole blob, so merge into the
      // current settings to avoid clobbering other keys.
      const settings = Object.assign({}, this.userSettings, { [SETTINGS_KEY]: windowState });
      this.$store.dispatch('updateUserSettings', settings)
        .then((response) => {
          this.$store.commit('setUserSettings', response.data);
        })
        .catch(() => {
          // non-critical: ignore persistence failures
        });
    },
    getMainArea() {
      // Standard app layout, or the content editor's own split layout
      // (FullPageSourceEditorV2 renders without #mainBodyScrollArea). The two
      // ids never coexist on one route.
      return document.getElementById('mainBodyScrollArea')
        || document.getElementById('editorMainArea');
    },
    updateMainAreaRect() {
      this.$nextTick(() => {
        const el = this.getMainArea();
        this.syncMainAreaObserverTarget();
        if (el) {
          this.mainAreaRect = el.getBoundingClientRect();
          return;
        }
        // No app scroll area (e.g. the content editor renders its own layout).
        // Anchor below the page's .header-area so the fullscreen overlay sits
        // under the header, mirroring how #mainBodyScrollArea does on /sites.
        const header = document.querySelector('.header-area');
        const top = header ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;
        this.mainAreaRect = {
          top,
          left: 0,
          right: window.innerWidth,
          bottom: window.innerHeight,
          width: window.innerWidth,
          height: window.innerHeight - top,
        };
      });
    },
    handleLayoutShift() {
      this.updateMainAreaRect();
      // Banners (e.g. Appcues) animate in, and neither observer fires during a
      // position-only CSS transition of a body sibling, so the first
      // measurement can capture an intermediate position. Re-measure again
      // after typical entrance animations settle.
      this._rectSettleTimers.forEach(clearTimeout);
      this._rectSettleTimers = [
        setTimeout(this.updateMainAreaRect, 300),
        setTimeout(this.updateMainAreaRect, 800),
      ];
    },
    setupLayoutObservers() {
      this.teardownLayoutObservers();
      if (typeof ResizeObserver !== 'undefined') {
        this._mainAreaResizeObserver = new ResizeObserver(() => this.debouncedLayoutShift());
      }
      if (typeof MutationObserver !== 'undefined') {
        // Appcues appends its banner as a direct child of <body> (outside
        // #app) and may offset content via body style/class. Non-subtree, so
        // ordinary app DOM churn doesn't fire it.
        this._bodyMutationObserver = new MutationObserver(() => this.debouncedLayoutShift());
        this._bodyMutationObserver.observe(document.body, {
          childList: true,
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
      }
      this.syncMainAreaObserverTarget();
    },
    syncMainAreaObserverTarget() {
      if (!this._mainAreaResizeObserver) return;
      const el = this.getMainArea();
      if (el === this._observedMainAreaEl) return;
      this._mainAreaResizeObserver.disconnect();
      this._observedMainAreaEl = el || null;
      if (!el) return;
      try {
        // border-box: applyMainAreaSqueeze writes paddingRight, which changes
        // the content box but not the border box, so observing border-box
        // avoids self-triggered fires from our own squeeze writes.
        this._mainAreaResizeObserver.observe(el, { box: 'border-box' });
      } catch (e) {
        this._mainAreaResizeObserver.observe(el);
      }
    },
    teardownLayoutObservers() {
      if (this._mainAreaResizeObserver) {
        this._mainAreaResizeObserver.disconnect();
        this._mainAreaResizeObserver = null;
      }
      if (this._bodyMutationObserver) {
        this._bodyMutationObserver.disconnect();
        this._bodyMutationObserver = null;
      }
      this._observedMainAreaEl = null;
      this._rectSettleTimers.forEach(clearTimeout);
      this._rectSettleTimers = [];
      if (this.debouncedLayoutShift) this.debouncedLayoutShift.cancel();
    },
    applyMainAreaSqueeze() {
      const el = this.getMainArea();
      if (!el) {
        this.updateMainAreaRect();
        return;
      }
      const squeeze = this.isChatbox && this.docked === 'right';
      // Clamp like overlayStyle so an oversized saved width can't squeeze the
      // page past the rendered panel (paddingRight doesn't affect the border
      // box, so measuring here is feedback-free).
      const w = Math.min(Math.max(this.sidebarWidth, MIN_WIDTH), el.getBoundingClientRect().width);
      el.style.paddingRight = squeeze ? `${w}px` : '';
      el.style.transition = 'padding-right 0.2s ease';
      // Fixed-position UI (e.g. .async-tasks-popup) can't follow the padding
      // squeeze; expose the docked width so it can offset itself.
      document.documentElement.style.setProperty('--rocky-sidebar-width', squeeze ? `${w}px` : '0px');
      this.updateMainAreaRect();
    },
    releaseMainAreaSqueeze() {
      const el = this.getMainArea();
      if (el) {
        el.style.paddingRight = '';
      }
      document.documentElement.style.setProperty('--rocky-sidebar-width', '0px');
    },
    handleIframeMessage(event) {
      if (event.origin !== this.POP_ASSISTANT_BASE_URL) return;
      const data = event.data;
      if (!data || typeof data !== 'object' || !data.type) return;

      const handlers = {
        minimize: this.handleMinimize,
        fullscreen: this.handleFullscreen,
        close: this.handleClose,
        resize: this.handleResize,
        'vue-router-push': this.handleVueRouterPush,
        refresh: this.handleRefresh,
      };
      const handler = handlers[data.type];
      if (handler) handler(data.payload);
    },
    handleMinimize(payload) {
      if (payload && typeof payload.width === 'number' && payload.width > 0) {
        this.sidebarWidth = payload.width;
      }
      // Explicit user choice — stop following the route default.
      this.routeDefaultActive = false;
      this.mode = 'chatbox';
      this.saveWindowState();
    },
    handleResize(payload) {
      if (!payload) return;
      if (typeof payload.width === 'number' && payload.width > 0) {
        this.sidebarWidth = payload.width;
      }
      // Always re-apply, even when width is unchanged — Rocky may repeat resize
      // messages after its own layout settles.
      if (this.showAiAssistantIframe) this.applyMainAreaSqueeze();
      this.saveWindowState();
    },
    handleFullscreen() {
      // Explicit user choice — stop following the route default.
      this.routeDefaultActive = false;
      this.mode = 'fullscreen';
      this.saveWindowState();
    },
    handleClose() {
      this.showAiAssistantIframe = false;
    },
    handleVueRouterPush(payload) {
      if (!payload) return;
      this.$router.push(payload).catch(() => {});
    },
    toggleChatbox() {
      // Explicit user choice — stop following the route default.
      this.routeDefaultActive = false;
      this.mode = this.isChatbox ? 'fullscreen' : 'chatbox';
    },
    // Open the panel from elsewhere (via this.$root.$refs.popAssistantButton), as the FAB does;
    // a no-op when the visitor has no access to the assistant.
    open() {
      if (!this.isReady) return false;
      this.showAiAssistantIframe = true;
      return true;
    },
    handleRefresh() {
      this.$store.dispatch("loadUser").then(response => {
        this.$store.commit("setUser", response.data.user);
      });
      this.$store.dispatch("loadSites").then(response => {
        if (response.data && response.data.status === "SUCCESS") {
          this.$store.commit("setSites", response.data.sites);
        }
      });
      this.$store.commit('triggerAsyncTasksRefresh');
      this.$store.commit('triggerRockyRefresh');
    },
    onTopbarMouseDown(event) {
      if (!this.isChatbox) return;
      if (event.target.closest && event.target.closest('.ai-assistant-iframe-action')) return;
      if (event.button !== 0) return;
      event.preventDefault();
      this.beginDrag('move', event);
    },
    onTopbarDoubleClick(event) {
      if (event.target.closest && event.target.closest('.ai-assistant-iframe-action')) return;
      this.toggleChatbox();
    },
    onLeftResizeMouseDown(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.beginDrag('resize-left', event);
    },
    onCornerResizeMouseDown(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.beginDrag('resize-corner', event);
    },
    onTopResizeMouseDown(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.beginDrag('resize-top', event);
    },
    onBottomResizeMouseDown(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.beginDrag('resize-bottom', event);
    },
    onRightResizeMouseDown(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.beginDrag('resize-right', event);
    },
    beginDrag(kind, event) {
      const overlayEl = event.currentTarget.parentElement;
      const rect = overlayEl.getBoundingClientRect();
      this.dragState = {
        kind,
        startX: event.clientX,
        startY: event.clientY,
        startPanelX: rect.left,
        startPanelY: rect.top,
        startW: rect.width,
        startH: rect.height,
      };
      window.addEventListener('mousemove', this.onDragMove);
      window.addEventListener('mouseup', this.onDragEnd);
    },
    onDragMove(event) {
      const ds = this.dragState;
      if (!ds) return;
      const dx = event.clientX - ds.startX;
      const dy = event.clientY - ds.startY;
      const r = this.mainAreaRect;
      const dockedMaxW = r ? r.width : window.innerWidth;
      const floatingMaxW = window.innerWidth;
      const floatingMaxH = window.innerHeight;

      if (ds.kind === 'move') {
        if (this.docked !== null) {
          this.docked = null;
        }
        this.floatingX = ds.startPanelX + dx;
        this.floatingY = ds.startPanelY + dy;
        if (this.floatingHeight === null) this.floatingHeight = ds.startH;
      } else if (ds.kind === 'resize-left') {
        const maxW = this.docked === 'right' ? dockedMaxW : floatingMaxW;
        const newW = Math.min(Math.max(MIN_WIDTH, ds.startW - dx), maxW);
        this.sidebarWidth = newW;
        if (this.docked === null) {
          this.floatingX = ds.startPanelX + (ds.startW - newW);
        } else if (this.showAiAssistantIframe) {
          this.applyMainAreaSqueeze();
        }
      } else if (ds.kind === 'resize-corner') {
        const newW = Math.min(Math.max(MIN_WIDTH, ds.startW + dx), floatingMaxW);
        const newH = Math.min(Math.max(MIN_HEIGHT, ds.startH + dy), floatingMaxH);
        this.sidebarWidth = newW;
        this.floatingHeight = newH;
      } else if (ds.kind === 'resize-bottom') {
        const newH = Math.min(Math.max(MIN_HEIGHT, ds.startH + dy), floatingMaxH);
        this.floatingHeight = newH;
      } else if (ds.kind === 'resize-top') {
        const newH = Math.min(Math.max(MIN_HEIGHT, ds.startH - dy), floatingMaxH);
        this.floatingHeight = newH;
        this.floatingY = ds.startPanelY + (ds.startH - newH);
      } else if (ds.kind === 'resize-right') {
        const newW = Math.min(Math.max(MIN_WIDTH, ds.startW + dx), floatingMaxW);
        this.sidebarWidth = newW;
      }
    },
    onDragEnd() {
      const ds = this.dragState;
      window.removeEventListener('mousemove', this.onDragMove);
      window.removeEventListener('mouseup', this.onDragEnd);
      this.dragState = null;
      if (!ds) return;

      if (ds.kind === 'move' && this.docked === null && this.mainAreaRect) {
        const r = this.mainAreaRect;
        const rightEdge = (this.floatingX || 0) + this.sidebarWidth;
        if (Math.abs(rightEdge - r.right) <= SNAP_THRESHOLD) {
          this.docked = 'right';
          this.floatingX = null;
          this.floatingY = null;
          this.floatingHeight = null;
        }
      }

      if (this.showAiAssistantIframe) this.applyMainAreaSqueeze();
      this.saveWindowState();
      this.sendLayoutToIframe();
    },
    postToIframe(type, payload) {
      const iframe = this.$refs.popAssistantIframe;
      if (!iframe || !iframe.contentWindow) return;
      const message = payload === undefined ? { type } : { type, payload };
      iframe.contentWindow.postMessage(message, this.POP_ASSISTANT_BASE_URL);
      console.log('[POP] Sent to Rocky on ' + this.POP_ASSISTANT_BASE_URL, message)
    },
    // Screen-ready messages are stateful: buffer the latest one (even while the
    // iframe is unmounted) so a Rocky panel opened or reloaded after the emit
    // still receives the current screen.
    postScreenToIframe(type, payload) {
      this._lastScreenReady = { type, payload };
      this.postToIframe(type, payload);
    },
    clearScreenFromIframe(type) {
      if (this._lastScreenReady && this._lastScreenReady.type === type) {
        this._lastScreenReady = null;
      }
    },
    postAuthToIframe(force = false) {
      const user = this.currentUser || null;
      // Only the identity fields matter to Rocky's auth. Deduping on these stops
      // unrelated user-object churn (e.g. a settings save replacing state.user)
      // from re-posting auth and looping. force=true bypasses it on iframe load.
      const sig = [
        this.currentToken || '',
        user && user.id != null ? user.id : (user && user.email ? user.email : ''),
        this.subAccountRelationId || '',
      ].join('|');
      if (!force && sig === this._lastAuthSig) return;
      this._lastAuthSig = sig;
      this.postToIframe('auth', {
        token: this.currentToken || null,
        user,
        subAccountRelationId: this.subAccountRelationId,
      });
    },
    postContextToIframe(route) {
      const r = route || this.$route;
      const params = r && r.params ? r.params : {};
      const query = r && r.query ? r.query : {};
      const isAccountSettings = r && r.name === 'UserProfile';
      const isEditor = r && r.name === 'FullPageSourceEditor';
      const rb = this.$store.getters.rockyReadback || {};
      this.postToIframe('pop-context', {
        siteId: params.siteId || null,
        pageId: params.pageId || null,
        // The Content Editor route carries pageRunId in the query, not params.
        pageRunId: params.pageRunId || query.pageRunId || null,
        routeName: r ? r.name : null,
        mainTab: isAccountSettings ? (query.mainTab || null) : null,
        subTab: isAccountSettings ? (query.subTab || null) : null,
        // AI Writer / Content Editor context. editorMode/aiRunId come from the
        // route query; sourceType/activeScreen come from the editor's readback.
        editorMode: isEditor ? (query.editorMode || null) : null,
        aiRunId: isEditor ? (query.aiRunId || null) : null,
        sourceType: isEditor ? (rb.sourceType != null ? rb.sourceType : null) : null,
        activeScreen: isEditor ? (rb.activeScreen || null) : null,
      });
    },
    sendLayoutToIframe() {
      if (!this.showAiAssistantIframe) return;
      this.postToIframe('pop-layout', {
        mode: this.mode,
        width: this.isChatbox ? this.sidebarWidth : null,
        height: this.isChatbox && this.docked === null ? this.floatingHeight : null,
        docked: this.docked,
        floating: this.docked === null,
      });
    },
    onIframeLoad() {
      // Force: a freshly (re)loaded iframe always needs auth, even if the
      // identity signature is unchanged from a previous load.
      this.postAuthToIframe(true);
      this.postContextToIframe();
      this.sendLayoutToIframe();
      // After auth/context: Rocky clears screen state on auth transitions and
      // reconciles the screen's siteId against pop-context.
      if (this._lastScreenReady) {
        this.postToIframe(this._lastScreenReady.type, this._lastScreenReady.payload);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.v-btn.v-btn.v-btn.ai-assistant-fab {
  position: fixed !important;
  bottom: 30px;
  right: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  z-index: 1000;

  // The AI Writer's sticky bottom bar (.aiw2-bottombar) sits at the bottom
  // edge on the content-editor route — nudge the button up a bit.
  &.is-above-editor-bottombar {
    bottom: 80px;
  }
}

.ai-assistant-fab-icon {
  color: white;
  font-size: 28px;
  line-height: 1;
}

.ai-assistant-iframe-overlay {
  position: fixed;
  background: var(--v-whiteColor-base);
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 1000;

  &.is-chatbox {
    border-left: 1px solid var(--v-grayColor-base);
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.08);
  }

  &.is-floating {
    border: 1px solid var(--v-grayColor-base);
    border-radius: 6px;
    overflow: hidden;
  }

  &.is-dragging {
    user-select: none;
  }
}

.ai-assistant-iframe-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: var(--v-cardBg-base);
  border-bottom: 1px solid var(--v-grayColor-base);
  flex-shrink: 0;

  &.is-draggable {
    cursor: grab;
    user-select: none;
  }

  &.is-draggable:active {
    cursor: grabbing;
  }
}

.ai-assistant-iframe-logo {
  height: 24px;
  width: auto;
  pointer-events: none;
}

.ai-assistant-iframe-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ai-assistant-iframe-action {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--v-mainColor-base);
  padding: 6px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  i {
    font-size: 18px;
  }
}

.ai-assistant-iframe {
  flex-grow: 1;
  width: 100%;
  border: 0;
}

.ai-assistant-resize-handle {
  position: absolute;
  z-index: 2;
  background: transparent;

  &.handle-left {
    top: 0;
    left: 0;
    width: 6px;
    height: 100%;
    cursor: ew-resize;
  }

  &.handle-top {
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    cursor: ns-resize;
  }

  &.handle-bottom {
    bottom: 0;
    left: 0;
    width: 100%;
    height: 6px;
    cursor: ns-resize;
  }

  &.handle-right {
    top: 0;
    right: 0;
    width: 6px;
    height: 100%;
    cursor: ew-resize;
  }

  &.handle-corner {
    bottom: 0;
    right: 0;
    width: 14px;
    height: 14px;
    cursor: nwse-resize;
    z-index: 3;
  }
}
</style>
